import React, { ComponentType, memo, PropsWithoutRef } from "react";

import MapViewState from "@mapmycustomers/shared/types/viewModel/MapViewState";

import IMapViewContext from "@app/component/view/types/IMapViewContext";
import useMapView from "@app/component/view/utils/useMapView";
import arePropsEqual from "@app/util/react/arePropsEqual";

const mapViewProps: Array<keyof IMapViewContext> = ["viewState"];

// This is a crazy optimization for crazy cases. Now exists only for experimental purposes.
//
// The problem:
//   Currently many components are using useMapView hook. But it has a drawback. When user drags
//   the map, we're updating viewState no matter what. (Usually in applyMapViewSettings reducer.)
//   And hence ViewState instance is always new after that, the IMapViewContext object is also
//   re-created. Hence, we have new state, and react re-renders all components which are using
//   useContext with that context.
//
//   Which is unwanted sometimes. Say, if filters were not changed, then viewState.filters
//   object will likely be the same. And we don't need to re-render any components which are
//   only using filters from the viewState. However, react doesn't know anything about context
//   usage in components and hence it still re-renders them when anything is changed in context.
//
// This is a known problem and there are some ways to solve it. Read more here:
// https://blog.axlight.com/posts/4-options-to-prevent-extra-rerenders-with-react-context/
//
// What we do with this HOC is kind of an option #2 from that article.
// Instead of using useMapView directly in component, we suggest to wrap it with this HOC
// and receive all context fields as props.
//
// But this is only step one. To make it truly optimal, we require to specify the 2nd
// argument in this HOC - a list of MapViewState fields a component uses. Component will
// only re-render when any of these fields change.
function withMapView<P extends IMapViewContext = IMapViewContext>(
  WrappedComponent: ComponentType<P>,
  compareViewStateFields: Array<keyof MapViewState>
) {
  const MemoizedWrappedComponent = memo(WrappedComponent, (prevProps: P, nextProps: P) => {
    if (prevProps.viewState && nextProps.viewState) {
      for (let i = 0; i < compareViewStateFields.length; i++) {
        const currentKey = compareViewStateFields[i];
        if (!Object.is(prevProps.viewState[currentKey], nextProps.viewState[currentKey])) {
          return false;
        }
      }
    }
    return arePropsEqual(prevProps, nextProps, mapViewProps);
  });

  const ComponentWithMapView = (props: Omit<P, keyof IMapViewContext>) => {
    const mapView: IMapViewContext = useMapView();
    const finalProps = { ...mapView, ...props } as PropsWithoutRef<P>;
    return <MemoizedWrappedComponent {...finalProps} {...{}} />;
  };

  const displayName = WrappedComponent.displayName || WrappedComponent.name || "Component";
  ComponentWithMapView.displayName = `withMapView(${displayName})`;

  return ComponentWithMapView;
}

export default withMapView;
